import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "react-query";
import { useToast } from "@/contexts/ToastContext";
import { useAccessControl } from "@/hooks/useAccessControl";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { LoadingSpinner } from "@/components/ui/loading-spinner";
import { Pagination } from "@/components/ui/pagination";
import { Plus, Edit2, Trash2, Briefcase, Eye, EyeOff } from "lucide-react";
import { jobTitlesApi, type JobTitle } from "@/lib/api/job-titles";
import { useJobTitles, JOB_TITLES_QUERY_KEY } from "@/hooks/useJobTitles";
import { cn } from "@/lib/utils";

const JobTitlesSettingsPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isTenantAdmin } = useAccessControl();
  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  const { data: jobTitles = [], isLoading: loading } = useJobTitles(includeInactive, {
    enabled: isTenantAdmin,
  });

  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [jobTitleToDelete, setJobTitleToDelete] = useState<JobTitle | null>(null);
  const [deleting, setDeleting] = useState(false);

  const handleEdit = (jobTitle: JobTitle) => {
    navigate(`/setup/job-titles/${jobTitle.id}/edit`);
  };

  const openDeleteDialog = (jobTitle: JobTitle) => {
    setJobTitleToDelete(jobTitle);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!jobTitleToDelete) return;

    try {
      setDeleting(true);
      await jobTitlesApi.delete(jobTitleToDelete.id);
      toast.success(`"${jobTitleToDelete.name}" has been deleted.`);
      queryClient.invalidateQueries(JOB_TITLES_QUERY_KEY);
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Unable to delete job title. Please try again.");
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setJobTitleToDelete(null);
    }
  };

  const filteredJobTitles = useMemo(() => {
    return jobTitles.filter((title) =>
      title.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [jobTitles, searchQuery]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, includeInactive]);

  // Paginate the filtered results
  const paginatedJobTitles = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredJobTitles.slice(startIndex, endIndex);
  }, [filteredJobTitles, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredJobTitles.length / itemsPerPage);

  if (!isTenantAdmin) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-screen bg-[#F8F9FA]">
          <Card className="max-w-md p-8 text-center">
            <h2 className="mb-2 text-xl font-bold text-gray-900">Access Denied</h2>
            <p className="text-gray-600">
              You need administrator permissions to manage job titles.
            </p>
          </Card>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Job Titles"
          description="Manage professional job titles for your library staff"
          breadcrumbItems={[{ label: "Setup", href: "/setup" }, { label: "Job Titles" }]}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          searchPlaceholder="Search job titles..."
        />

        <div className="flex flex-col justify-between gap-4 mb-6 sm:flex-row sm:items-center">
          <div className="flex items-center gap-3">
            <Checkbox
              id="includeInactive"
              checked={includeInactive}
              onCheckedChange={(checked) => setIncludeInactive(checked === true)}
            />
            <Label
              htmlFor="includeInactive"
              className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer"
            >
              {includeInactive ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
              Show inactive job titles
            </Label>
          </div>
          <Button onClick={() => navigate("/setup/job-titles/new")} className="shadow-sm">
            <Plus className="w-4 h-4 mr-2" />
            Add Job Title
          </Button>
        </div>

        <Card className="overflow-hidden bg-white shadow-sm">
          {loading ? (
            <div className="py-16">
              <LoadingSpinner size="lg" label="Loading job titles..." />
            </div>
          ) : filteredJobTitles.length === 0 ? (
            <div className="py-16 text-center">
              <div className="flex items-center justify-center w-16 h-16 p-4 mx-auto mb-4 bg-gray-100 rounded-full">
                <Briefcase className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {searchQuery ? "No matching job titles" : "No job titles yet"}
              </h3>
              <p className="max-w-sm mx-auto mb-6 text-sm text-gray-500">
                {searchQuery
                  ? "Try a different search term."
                  : "Create your first job title to categorize your staff."}
              </p>
              {!searchQuery && (
                <Button onClick={() => navigate("/setup/job-titles/new")} variant="outline">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Your First Job Title
                </Button>
              )}
            </div>
          ) : (
            <>
              <div className="divide-y divide-gray-100">
                {paginatedJobTitles.map((jobTitle) => (
                  <div
                    key={jobTitle.id}
                    className={cn(
                      "p-5 hover:bg-gray-50 transition-colors",
                      !jobTitle.isActive && "bg-gray-50/50"
                    )}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">{jobTitle.name}</h3>
                          <span
                            className={cn(
                              "px-2.5 py-0.5 rounded-full text-xs font-medium",
                              jobTitle.isActive
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-200 text-gray-600"
                            )}
                          >
                            {jobTitle.isActive ? "Active" : "Inactive"}
                          </span>
                        </div>
                        {jobTitle._count && jobTitle._count.staff > 0 && (
                          <p className="text-sm text-gray-500">
                            {jobTitle._count.staff} staff member
                            {jobTitle._count.staff !== 1 ? "s" : ""} assigned
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(jobTitle)}
                          className="gap-1.5"
                        >
                          <Edit2 className="w-4 h-4" />
                          <span className="hidden sm:inline">Edit</span>
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openDeleteDialog(jobTitle)}
                          className="gap-1.5 text-red-600 hover:text-red-700 hover:bg-red-50 hover:border-red-200"
                          disabled={!!(jobTitle._count?.staff && jobTitle._count.staff > 0)}
                        >
                          <Trash2 className="w-4 h-4" />
                          <span className="hidden sm:inline">Delete</span>
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              {filteredJobTitles.length > 0 && (
                <div className="px-5 py-4 border-t border-gray-200">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    totalItems={filteredJobTitles.length}
                    itemsPerPage={itemsPerPage}
                    onPageChange={setCurrentPage}
                    onItemsPerPageChange={setItemsPerPage}
                  />
                </div>
              )}
            </>
          )}
        </Card>
      </PageContainer>

      <ConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={`Delete "${jobTitleToDelete?.name}"?`}
        description={
          jobTitleToDelete?._count?.staff && jobTitleToDelete._count.staff > 0 ? (
            <>
              <p className="mb-2 font-medium text-red-600">
                Cannot delete: {jobTitleToDelete._count.staff} staff member
                {jobTitleToDelete._count.staff !== 1 ? "s are" : " is"} assigned to this job title.
              </p>
              <p>Please reassign staff to another job title first.</p>
            </>
          ) : (
            <>
              <p className="mb-2">
                This will <strong>permanently delete</strong> this job title. This action cannot be
                undone.
              </p>
            </>
          )
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={handleDelete}
        variant="danger"
        loading={deleting}
      />
    </AppLayout>
  );
};

export default JobTitlesSettingsPage;
